import { useEffect, useState, useCallback } from "react";
import { Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const PushToggle = ({ compact = false }) => {
  const [state, setState] = useState("loading"); // loading | unsupported | denied | on | off

  const getRegistration = useCallback(async () => {
    return navigator.serviceWorker.register("/sw.js");
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setState("unsupported");
      return;
    }
    if (Notification.permission === "denied") {
      setState("denied");
      return;
    }
    getRegistration()
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setState(sub ? "on" : "off"))
      .catch(() => setState("off"));
  }, [getRegistration]);

  const enable = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setState("denied");
        toast.error("Notifications were blocked by the browser");
        return;
      }
      const { data } = await api.get("/push/vapid-public-key");
      const reg = await getRegistration();
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.key),
      });
      await api.post("/push/subscribe", { subscription: sub.toJSON() });
      setState("on");
      toast.success("Notifications enabled on this device");
    } catch (e) {
      toast.error("Could not enable notifications");
    }
  };

  const disable = async () => {
    try {
      const reg = await getRegistration();
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setState("off");
      toast.success("Notifications disabled on this device");
    } catch (e) {
      toast.error("Could not disable notifications");
    }
  };

  if (state === "unsupported" || state === "loading") return null;

  if (state === "denied") {
    if (compact) return null;
    return (
      <p className="text-xs text-muted-foreground" data-testid="push-denied-note">
        Notifications blocked — enable them in your browser settings.
      </p>
    );
  }

  return state === "on" ? (
    <Button variant="outline" size="sm" className={compact ? "rounded-xl" : "w-full rounded-xl"} onClick={disable} data-testid="push-disable-button">
      <BellOff className="h-4 w-4 mr-2" /> {compact ? "Alerts on" : "Notifications on"}
    </Button>
  ) : (
    <Button variant="outline" size="sm" className={`${compact ? "rounded-xl" : "w-full rounded-xl"} border-primary text-primary hover:bg-primary hover:text-white transition-colors`} onClick={enable} data-testid="push-enable-button">
      <Bell className="h-4 w-4 mr-2" /> {compact ? "Alerts" : "Enable notifications"}
    </Button>
  );
};
